import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import type { InitiativeWriteInput } from '@complan/contracts';
import {
  COVERAGE_FACTOR_MAX,
  COVERAGE_FACTOR_MIN,
  FIELD_FORCE_SCOPE_MAX_ENTRIES,
  FREE_TEXT_MAX_LENGTH,
  FREQUENCY_MIN,
  IMPACTED_POPULATION_COUNT_MAX,
  IMPACTED_POPULATION_COUNT_MIN,
  POPULATION_OVERRIDE_REASON_MAX_LENGTH,
  POPULATION_OVERRIDE_REASON_MIN_LENGTH,
  SHORT_TEXT_MAX_LENGTH,
} from '@complan/contracts';
import type { FieldForceRole } from '@complan/domain';
import { DateRangeField } from '@/shared/components/DateRangeField';
import { Button } from '@/components/ui/button';
import { CheckboxGroup, ConditionalBlock, ControlledSelect, Field, OverrideBadge, TextInput } from './fields';
import type { IntakeOptionSets, StoreTypeOption } from './intake-options';

type FormValues = InitiativeWriteInput;
type ScopeEntry = NonNullable<FormValues['fieldForceScope']>[number];

/**
 * Step 5 - Field Force involvement (UI spec §6.2, R5.1, R5.2; Manpower R4.1-R4.6).
 *
 * Flow: FF involved (owned by the wizard's involvement toggle) → role selection
 * (ADE / BDE) → one or more activity blocks per role. Each activity captures the activity
 * type, the Region and Population_Unit the demand belongs to, the
 * store-type/segment multi-select (each option showing its POS count), the derived
 * POS count, a manual override with a required reason and the `badge-ovr` marker,
 * a coverage factor, a frequency and the optional activity dates.
 *
 * ## Every input here persists
 *
 * This step used to hold the §6.2 demand inputs in local component state, because
 * Foundation's write schema had no field for them. Manpower closed that gap on
 * both sides - the schema (task 5.3) and the write repository (task 8.1) - so they
 * are now form-model fields on the scope entry and travel with the submission.
 *
 * That matters beyond tidiness: `readDemandDrivers` treats a scope entry as a
 * Field_Force_Demand_Driver only when Population_Unit,
 * Impacted_Population_Count, Coverage_Factor and Frequency are *all* present, so
 * an entry missing any of them contributes no Demand_Hours and never reaches a
 * Capacity & Utilisation figure. A scope entry recorded for governance tracking
 * alone remains legitimate (Foundation R5.2) - which is why none of these fields
 * is required here.
 *
 * ## Out of scope here (UI spec §0.1, §6.2)
 *
 * The live calculation preview card and the forward capacity-impact warning belong
 * to the Manpower screens; this step captures the inputs, and the Demand Explorer
 * and Capacity & Utilisation screens report what they produce.
 */
const FF_ROLES: readonly FieldForceRole[] = ['ade', 'bde'];

/**
 * The demand inputs "Add activity types" copies from the role's last activity
 * into each new one. Material, Work order and Information contribution are not
 * copied: they describe one activity, not the shared population.
 */
const SHARED_DEMAND_INPUTS = [
  'audienceId',
  'regionId',
  'populationUnitId',
  'posSegmentIds',
  'impactedPopulationCount',
  'populationOverrideReason',
  'coverageFactor',
  'frequency',
  'activityStartDate',
  'activityEndDate',
] as const satisfies readonly (keyof ScopeEntry)[];

/**
 * ## Several activities per role
 *
 * Each scope entry is one Field_Force_Demand_Driver with its own activity type,
 * so a role can carry several activities: "+ Add activity" appends a blank one,
 * and "Add activity types" creates one per selected type with the demand inputs
 * copied from the role's last activity (each type still resolves its own minutes
 * per unit from the Effort Matrix). One activity type appears at most once per
 * role; the server enforces the same rule.
 */
export function FieldForceStep({ options }: { readonly options: IntakeOptionSets }): React.JSX.Element {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const scope = useController<FormValues>({ name: 'fieldForceScope', control });
  const entries = (scope.field.value as FormValues['fieldForceScope'] | undefined) ?? [];

  // The initiative's Timing (step 2, before this step). A new activity starts
  // with its activity window set to the same dates; the user can narrow or clear
  // it. Equal dates narrow nothing, so the pre-fill never changes demand.
  const [initiativeStart, initiativeEnd] = useWatch({ control, name: ['startDate', 'endDate'] });
  const timing =
    initiativeStart && initiativeEnd
      ? {
          activityStartDate: initiativeStart as ScopeEntry['activityStartDate'],
          activityEndDate: initiativeEnd as ScopeEntry['activityEndDate'],
        }
      : { activityStartDate: null, activityEndDate: null };

  const selectedRoles = new Set(entries.map((entry) => entry.fieldForceRole));
  const roleLabel = (role: FieldForceRole): string =>
    role === 'ade' ? t('intake.fieldForce.roleAde') : t('intake.fieldForce.roleBde');
  const typeName = (id: string): string =>
    options.fieldForceActivityTypes.find((option) => option.id === id)?.label ?? '';

  function blankEntry(role: FieldForceRole, activityTypeId = ''): ScopeEntry {
    return {
      fieldForceRole: role,
      activityTypeId,
      audienceId: null,
      hasMaterial: false,
      workOrder: null,
      informationContribution: null,
      // The demand inputs start absent rather than zeroed: "no demand input
      // entered yet" and "a coverage factor of zero" are different statements
      // (Assumption A15), and only the latter contributes fixed overhead.
      populationUnitId: null,
      regionId: null,
      posSegmentIds: [],
      impactedPopulationCount: null,
      populationOverrideReason: null,
      coverageFactor: null,
      frequency: null,
      ...timing,
    };
  }

  function toggleRole(role: FieldForceRole, checked: boolean): void {
    scope.field.onChange(
      checked ? [...entries, blankEntry(role)] : entries.filter((entry) => entry.fieldForceRole !== role),
    );
  }

  function patchAt(index: number, next: Partial<ScopeEntry>): void {
    scope.field.onChange(entries.map((entry, i) => (i === index ? { ...entry, ...next } : entry)));
  }

  function removeAt(index: number): void {
    scope.field.onChange(entries.filter((_entry, i) => i !== index));
  }

  /** One activity per selected type, sharing the last activity's demand inputs. */
  function addTypes(role: FieldForceRole, typeIds: readonly string[]): void {
    if (typeIds.length === 0) return;
    const lastIndex = entries.reduce((last, entry, i) => (entry.fieldForceRole === role ? i : last), -1);
    const source = lastIndex >= 0 ? entries[lastIndex] : undefined;
    const shared: Partial<ScopeEntry> = {};
    if (source) {
      for (const key of SHARED_DEMAND_INPUTS) {
        Object.assign(shared, { [key]: key === 'posSegmentIds' ? [...(source.posSegmentIds ?? [])] : source[key] });
      }
    }
    let next = [...entries];
    let remaining = [...typeIds];
    // A last activity with no type yet takes the first selected type instead of
    // staying behind as an empty block.
    if (source && lastIndex >= 0 && (source.activityTypeId ?? '') === '') {
      next = next.map((entry, i) => (i === lastIndex ? { ...entry, activityTypeId: remaining[0] ?? '' } : entry));
      remaining = remaining.slice(1);
    }
    scope.field.onChange([
      ...next,
      ...remaining.map((id) => ({ ...blankEntry(role, id), ...shared })),
    ]);
  }

  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.fieldForce.heading')}</h2>

      <Field label={t('intake.fieldForce.roleLegend')}>
        <fieldset className="border-0 p-0">
          <legend className="sr-only">{t('intake.fieldForce.roleLegend')}</legend>
          <div className="flex gap-4">
            {FF_ROLES.map((role) => (
              <label key={role} className="flex items-center gap-2 text-[12px] text-txt">
                <input
                  type="checkbox"
                  checked={selectedRoles.has(role)}
                  onChange={(event) => toggleRole(role, event.target.checked)}
                />
                <span>{roleLabel(role)}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </Field>

      {FF_ROLES.filter((role) => selectedRoles.has(role)).map((role) => {
        const roleEntries = entries
          .map((entry, index) => ({ entry, index }))
          .filter(({ entry }) => entry.fieldForceRole === role);
        const usedTypes = new Set(roleEntries.map(({ entry }) => entry.activityTypeId).filter(Boolean));
        const unusedTypes = options.fieldForceActivityTypes.filter(
          (option) => !option.isInactive && !usedTypes.has(option.id),
        );
        return (
          <div key={role} className="mb-3">
            {roleEntries.map(({ entry, index }, position) => {
              const heading = entry.activityTypeId
                ? t('intake.fieldForce.activityHeadingWithType', {
                    role: roleLabel(role),
                    index: position + 1,
                    activity: typeName(entry.activityTypeId),
                  })
                : t('intake.fieldForce.activityHeading', { role: roleLabel(role), index: position + 1 });
              // Activity types already used by this role's other activities are
              // not offered again (one type per role).
              const typeOptions = options.fieldForceActivityTypes.filter(
                (option) => option.id === entry.activityTypeId || !usedTypes.has(option.id),
              );
              return (
                // Keyed on the count as well, so adding or removing an activity
                // remounts the blocks and no local state follows the wrong entry.
                <ConditionalBlock key={`${String(index)}-${String(entries.length)}`} className="mb-2">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-[12px] font-semibold text-txt">{heading}</h3>
                    {roleEntries.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('intake.fieldForce.removeActivityLabel', { name: heading })}
                        onClick={() => removeAt(index)}
                      >
                        {t('intake.fieldForce.removeActivity')}
                      </Button>
                    ) : null}
                  </div>

                  <Field label={t('intake.fieldForce.activityTypeLabel')} required requiredMark={t('intake.requiredMark')}>
                    <ControlledSelect
                      options={typeOptions}
                      placeholder={t('intake.fieldForce.activityTypePlaceholder')}
                      inactiveMarker={t('intake.inactiveMarker')}
                      value={entry.activityTypeId ?? ''}
                      onChange={(event) => patchAt(index, { activityTypeId: event.target.value })}
                    />
                  </Field>

                  <Field label={t('intake.fieldForce.hasMaterialLabel')}>
                    <label className="flex items-center gap-2 text-[12px] text-txt">
                      <input
                        type="checkbox"
                        checked={entry.hasMaterial ?? false}
                        onChange={(event) => patchAt(index, { hasMaterial: event.target.checked })}
                      />
                      <span>{t('common.yes')}</span>
                    </label>
                  </Field>

                  <OptionalTextField
                    label={t('intake.fieldForce.workOrderLabel')}
                    checkboxLabel={t('common.yes')}
                    placeholder={t('intake.fieldForce.workOrderPlaceholder')}
                    maxLength={SHORT_TEXT_MAX_LENGTH}
                    value={entry.workOrder ?? null}
                    onChange={(next) => patchAt(index, { workOrder: next })}
                  />

                  <OptionalTextField
                    label={t('intake.fieldForce.informationContributionLabel')}
                    checkboxLabel={t('common.yes')}
                    placeholder={t('intake.fieldForce.informationContributionPlaceholder')}
                    maxLength={FREE_TEXT_MAX_LENGTH}
                    value={entry.informationContribution ?? null}
                    onChange={(next) => patchAt(index, { informationContribution: next })}
                  />

                  <FieldForceDriverInputs
                    entry={entry}
                    options={options}
                    onPatch={(next) => patchAt(index, next)}
                  />
                </ConditionalBlock>
              );
            })}

            <AddActivities
              roleName={roleLabel(role)}
              unusedTypes={unusedTypes}
              atLimit={entries.length >= FIELD_FORCE_SCOPE_MAX_ENTRIES}
              onAddOne={() => scope.field.onChange([...entries, blankEntry(role)])}
              onAddTypes={(ids) => addTypes(role, ids)}
            />
          </div>
        );
      })}

      <p className="text-muted-foreground mt-2 text-[11px]">{t('intake.fieldForce.calcPreviewNote')}</p>
    </section>
  );
}

/**
 * The per-role footer: "+ Add activity" (one blank activity) and, collapsed
 * below it, "Add activity types", a checklist of the types the role does not use
 * yet that creates one activity per ticked type.
 */
function AddActivities({
  roleName,
  unusedTypes,
  atLimit,
  onAddOne,
  onAddTypes,
}: {
  readonly roleName: string;
  readonly unusedTypes: IntakeOptionSets['fieldForceActivityTypes'];
  readonly atLimit: boolean;
  readonly onAddOne: () => void;
  readonly onAddTypes: (ids: readonly string[]) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const noneLeft = unusedTypes.length === 0;
  return (
    <div className="flex flex-col gap-2 pl-[3px]">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={atLimit || noneLeft} onClick={onAddOne}>
          {t('intake.fieldForce.addActivity', { role: roleName })}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={open}
          disabled={atLimit || noneLeft}
          onClick={() => setOpen((value) => !value)}
        >
          {t('intake.fieldForce.addTypesToggle')}
        </Button>
        {noneLeft ? (
          <span className="text-muted-foreground text-[11px]">
            {t('intake.fieldForce.allTypesUsed', { role: roleName })}
          </span>
        ) : atLimit ? (
          <span className="text-muted-foreground text-[11px]">
            {t('intake.fieldForce.activityLimit', { max: FIELD_FORCE_SCOPE_MAX_ENTRIES })}
          </span>
        ) : null}
      </div>
      {open && !noneLeft && !atLimit ? (
        <div className="border-line rounded-[var(--radius-control)] border bg-panel p-2">
          <p className="text-muted-foreground mb-1 text-[11px]">
            {t('intake.fieldForce.addTypesHint', { role: roleName })}
          </p>
          <CheckboxGroup
            legend={t('intake.fieldForce.addTypesToggle')}
            options={unusedTypes}
            selected={picked}
            inactiveMarker={t('intake.inactiveMarker')}
            onToggle={(id, checked) =>
              setPicked((current) => (checked ? [...current, id] : current.filter((value) => value !== id)))
            }
          />
          <div className="mt-2">
            <Button
              type="button"
              size="sm"
              disabled={picked.length === 0}
              onClick={() => {
                onAddTypes(picked);
                setPicked([]);
                setOpen(false);
              }}
            >
              {t('intake.fieldForce.addTypesButton', { count: picked.length })}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A "Yes" checkbox that reveals a link-or-text input (Work order, Information
 * contribution). Unchecking hides the input and clears the value, so a hidden
 * field never submits stale text. A stored value keeps the box checked when an
 * existing initiative is edited.
 */
function OptionalTextField({
  label,
  checkboxLabel,
  placeholder,
  maxLength,
  value,
  onChange,
}: {
  readonly label: string;
  readonly checkboxLabel: string;
  readonly placeholder: string;
  readonly maxLength: number;
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
}): React.JSX.Element {
  const [checked, setChecked] = useState(value !== null);
  const shown = checked || value !== null;
  return (
    <Field label={label}>
      <label className="flex items-center gap-2 text-[12px] text-txt">
        <input
          type="checkbox"
          checked={shown}
          onChange={(event) => {
            setChecked(event.target.checked);
            if (!event.target.checked) onChange(null);
          }}
        />
        <span>{checkboxLabel}</span>
      </label>
      {shown ? (
        <div className="mt-2">
          <TextInput
            aria-label={label}
            placeholder={placeholder}
            maxLength={maxLength}
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value || null)}
          />
        </div>
      ) : null}
    </Field>
  );
}

/**
 * The POS count a set of selected segments derives (R4.2).
 *
 * Exported so the override rule below and any test read the same sum rather than
 * two implementations of it.
 */
export function derivedPosCount(
  storeTypes: readonly StoreTypeOption[],
  selectedIds: readonly string[],
): number {
  const selected = new Set(selectedIds);
  return storeTypes
    .filter((option) => selected.has(option.id))
    .reduce((sum, option) => sum + option.posCount, 0);
}

/**
 * Whether a manual Impacted_Population_Count counts as an override of the
 * POS_Segment-derived value (R4.3).
 *
 * The condition mirrors the server's `resolveFieldForceScopeEntries` exactly -
 * segments selected, a count entered, and that count differing from their sum -
 * so the form asks for a reason in precisely the cases the write path would refuse
 * without one. R4.3's final sentence is what makes the *selection* the
 * discriminator: a manual count on an entry naming no segments is a direct entry,
 * not an override, and needs no reason.
 *
 * The server still decides; this only spares the reader a rejected save.
 */
export function isPopulationOverride(
  storeTypes: readonly StoreTypeOption[],
  selectedIds: readonly string[],
  /**
   * As held by the form. The contract narrows this to a 2-decimal `DecimalText`
   * on submit; until then it is whatever the number input produced, so the
   * comparison coerces rather than assuming a shape.
   */
  impactedPopulationCount: string | number | null,
): boolean {
  if (selectedIds.length === 0 || impactedPopulationCount === null || impactedPopulationCount === '') {
    return false;
  }
  const entered = Number(impactedPopulationCount);
  if (!Number.isFinite(entered)) {
    return false;
  }
  return entered !== derivedPosCount(storeTypes, selectedIds);
}

/**
 * The store-type / derived-POS / override / coverage / frequency inputs of §6.2,
 * bound to the scope entry (Manpower R4.1-R4.6).
 *
 * The derived count is shown read-only and is never replaced: R4.3 requires both
 * the derived value and the override to be retained, so the manual count is a
 * separate field beside it rather than an edit of it.
 */
function FieldForceDriverInputs({
  entry,
  options,
  onPatch,
}: {
  readonly entry: ScopeEntry;
  readonly options: IntakeOptionSets;
  readonly onPatch: (next: Partial<ScopeEntry>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  const selectedSegments = entry.posSegmentIds ?? [];
  const derivedPos = derivedPosCount(options.storeTypes, selectedSegments);
  const override = isPopulationOverride(
    options.storeTypes,
    selectedSegments,
    entry.impactedPopulationCount ?? null,
  );
  const reasonLength = (entry.populationOverrideReason ?? '').trim().length;
  const reasonTooShort = override && reasonLength < POPULATION_OVERRIDE_REASON_MIN_LENGTH;
  const selectedSet = new Set(selectedSegments);

  /**
   * Selecting segments refreshes the derived count, and with it what counts as an
   * override. A count equal to the new sum stops being one, so its reason is
   * cleared rather than left to be dropped silently by the server.
   */
  function patchSegments(nextIds: readonly string[]): void {
    const nextDerived = derivedPosCount(options.storeTypes, nextIds);
    const stillOverride = isPopulationOverride(
      options.storeTypes,
      nextIds,
      entry.impactedPopulationCount ?? null,
    );
    onPatch({
      posSegmentIds: [...nextIds],
      // With segments selected and no manual count yet, the derived sum *is* the
      // count (R4.2), so it is filled in rather than left absent - an absent count
      // is what stops the entry being a demand driver at all.
      ...(entry.impactedPopulationCount === null && nextIds.length > 0
        ? { impactedPopulationCount: nextDerived.toFixed(2) }
        : {}),
      ...(stillOverride ? {} : { populationOverrideReason: null }),
    });
  }

  return (
    <>
      <Field label={t('intake.fieldForce.regionLabel')} hint={t('intake.fieldForce.regionHint')}>
        <ControlledSelect
          options={options.regions}
          placeholder={t('intake.fieldForce.regionPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={entry.regionId ?? ''}
          onChange={(event) => onPatch({ regionId: event.target.value || null })}
        />
      </Field>

      <Field
        label={t('intake.fieldForce.populationUnitLabel')}
        hint={t('intake.fieldForce.populationUnitHint')}
      >
        <ControlledSelect
          options={options.populationUnits}
          placeholder={t('intake.fieldForce.populationUnitPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={entry.populationUnitId ?? ''}
          onChange={(event) => onPatch({ populationUnitId: event.target.value || null })}
        />
      </Field>

      <Field label={t('intake.fieldForce.storeTypeLegend')}>
        <fieldset className="border-0 p-0">
          <legend className="sr-only">{t('intake.fieldForce.storeTypeLegend')}</legend>
          {options.storeTypes.length === 0 ? (
            <p className="text-muted-foreground text-[11.5px]">
              {t('intake.fieldForce.noStoreTypes')}
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-x-3 gap-y-1">
              {options.storeTypes.map((option) => (
                <label key={option.id} className="flex items-center gap-2 text-[12px] text-txt">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option.id)}
                    onChange={(event) =>
                      patchSegments(
                        event.target.checked
                          ? [...selectedSegments, option.id]
                          : selectedSegments.filter((id) => id !== option.id),
                      )
                    }
                  />
                  <span>
                    {option.label}
                    <span className="text-muted-foreground ml-1 tabular-nums">({option.posCount})</span>
                    {option.isInactive ? (
                      <span className="text-muted-foreground ml-1">{t('intake.inactiveMarker')}</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      </Field>

      {selectedSegments.length > 0 ? (
        <Field label={t('intake.fieldForce.derivedPosLabel')} hint={t('intake.fieldForce.derivedPosSource')}>
          <span className="text-[13px] font-semibold tabular-nums text-txt">
            {derivedPos}
            {override ? <OverrideBadge>{t('intake.fieldForce.overrideBadge')}</OverrideBadge> : null}
          </span>
        </Field>
      ) : null}

      <Field
        label={t('intake.fieldForce.impactedPopulationLabel')}
        hint={t('intake.fieldForce.impactedPopulationHint')}
      >
        <TextInput
          type="number"
          inputMode="decimal"
          min={IMPACTED_POPULATION_COUNT_MIN}
          max={IMPACTED_POPULATION_COUNT_MAX}
          step={1}
          placeholder={t('intake.fieldForce.overridePlaceholder')}
          value={entry.impactedPopulationCount ?? ''}
          onChange={(event) => {
            const raw = event.target.value;
            const next = raw === '' ? null : raw;
            const stillOverride = isPopulationOverride(options.storeTypes, selectedSegments, next);
            onPatch({
              impactedPopulationCount: next,
              ...(stillOverride ? {} : { populationOverrideReason: null }),
            });
          }}
        />
      </Field>

      {override ? (
        <>
          <p role="status" className="mb-1 text-[11.5px] text-[var(--warn)]">
            {t('intake.fieldForce.overrideWarning', { derived: derivedPos })}
          </p>
          <Field
            label={t('intake.fieldForce.overrideReasonLabel')}
            required
            requiredMark={t('intake.requiredMark')}
            {...(reasonTooShort
              ? { hint: t('intake.fieldForce.overrideReasonTooShort', {
                  min: POPULATION_OVERRIDE_REASON_MIN_LENGTH,
                }) }
              : {})}
          >
            <TextInput
              maxLength={POPULATION_OVERRIDE_REASON_MAX_LENGTH}
              placeholder={t('intake.fieldForce.overrideReasonPlaceholder')}
              value={entry.populationOverrideReason ?? ''}
              onChange={(event) =>
                onPatch({ populationOverrideReason: event.target.value || null })
              }
            />
          </Field>
        </>
      ) : null}

      <Field
        label={t('intake.fieldForce.coverageFactorLabel')}
        hint={t('intake.fieldForce.coverageFactorHint')}
      >
        <TextInput
          type="number"
          inputMode="decimal"
          min={COVERAGE_FACTOR_MIN}
          max={COVERAGE_FACTOR_MAX}
          step={0.01}
          value={entry.coverageFactor ?? ''}
          onChange={(event) =>
            onPatch({ coverageFactor: event.target.value === '' ? null : event.target.value })
          }
        />
      </Field>

      <Field label={t('intake.fieldForce.frequencyLabel')} hint={t('intake.fieldForce.frequencyHint')}>
        <TextInput
          type="number"
          inputMode="numeric"
          min={FREQUENCY_MIN}
          step={1}
          value={entry.frequency ?? ''}
          onChange={(event) =>
            onPatch({
              frequency: event.target.value === '' ? null : Number(event.target.value),
            })
          }
        />
      </Field>

      <Field
        label={`${t('intake.fieldForce.activityStartLabel')} / ${t('intake.fieldForce.activityEndLabel')}`}
        hint={t('intake.fieldForce.activityDatesHint')}
      >
        <DateRangeField
          value={
            entry.activityStartDate && entry.activityEndDate
              ? { startDate: entry.activityStartDate, endDate: entry.activityEndDate }
              : null
          }
          onChange={(range) =>
            onPatch({
              activityStartDate: (range?.startDate ?? null) as ScopeEntry['activityStartDate'],
              activityEndDate: (range?.endDate ?? null) as ScopeEntry['activityEndDate'],
            })
          }
          label={t('intake.fieldForce.activityDateRangeLabel')}
        />
      </Field>
    </>
  );
}
